import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Link, useParams } from "react-router-dom";
import {
  allSeasonAPI,
  EpisodeDetailsAPI,
  RecommendedTvShowsAPI,
  TvShowsDetailsAPI,
  TvShowsSimilarAPI,
} from "../constants/api";
import { AccordionSeason, Footer, Navbar } from "../components";
import EpisodePageSkeleton from "../components/EpisodePageSkeleton";
import { Star, Calendar, Clock, Play, Film, Tv } from "lucide-react";

function formatRuntime(runtime) {
  if (typeof runtime === "number" && runtime > 0) {
    const hours = Math.floor(runtime / 60);
    const minutes = runtime % 60;
    return `${hours}h ${minutes}min`;
  } else {
    return "N/A";
  }
}
const EpisodePage = () => {
  const { id, season_number, episode_number } = useParams();

  const [episodeDetails, setEpisodeDetails] = useState([]);
  const [details, setDetails] = useState([]);
  const [numberOfSeasons, setNumberOfSeasons] = useState(0);
  const [allSeasonsData, setAllSeasonsData] = useState([]);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [recommendedMovies, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [server1, setServer1] = useState(true);
  const [server2, setServer2] = useState(false);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Fetch all data in parallel
        const [episodeData, seriesDetails, similarData, recommendedData] = await Promise.all([
          EpisodeDetailsAPI(id, season_number, episode_number),
          TvShowsDetailsAPI(id),
          TvShowsSimilarAPI(id),
          RecommendedTvShowsAPI(id),
        ]);

        // Set state
        setEpisodeDetails(episodeData);
        setDetails(seriesDetails);
        setSimilarMovies(similarData?.results || []);
        setRecommended(recommendedData?.results || []);

        // Get number of seasons and fetch all seasons
        const seasonsCount = seriesDetails?.number_of_seasons || 0;
        setNumberOfSeasons(seasonsCount);

        if (seasonsCount > 0) {
          const seasonsPromises = [];
          for (let seasonNumber = 1; seasonNumber <= seasonsCount; seasonNumber++) {
            seasonsPromises.push(
              allSeasonAPI(id, seasonNumber).catch((error) => {
                console.error(`Error fetching season ${seasonNumber}:`, error);
                return null;
              })
            );
          }
          const seasonsResults = await Promise.all(seasonsPromises);
          setAllSeasonsData(seasonsResults.filter(Boolean));
        }
      } catch (error) {
        console.error("Error fetching episode data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [id, season_number, episode_number]);
  const formattedDate = (dateString) => {
    if (!dateString) {
      return "";
    }

    const date = new Date(Date.parse(dateString));
    const options = { year: "numeric", month: "long", day: "numeric" };

    return date.toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <EpisodePageSkeleton />
        <Footer />
      </>
    );
  }

  return (
    <div>
      <Navbar />
      <section className="bg-black min-h-screen">
        {/* Hero Section with Backdrop */}
        {episodeDetails?.still_path && (
          <div className="relative w-full h-[auto] min-h-[50vh] md:h-[70vh] overflow-hidden pt-16 md:pt-0">
            <div 
              className="absolute inset-0 bg-cover bg-no-repeat"
              style={{
                backgroundImage: `url(https://image.tmdb.org/t/p/original${episodeDetails.still_path})`,
                backgroundPosition: 'center top',
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
            
            {/* Content Overlay */}
            <div className="relative z-10 h-full flex items-center md:items-end padding py-8 md:pb-12">
              <div className="w-full max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-end">
                  {/* Episode Still Image */}
                  <div className="flex-shrink-0">
                    {episodeDetails?.still_path ? (
                      <img
                        src={`https://image.tmdb.org/t/p/w500${episodeDetails.still_path}`}
                        alt={episodeDetails?.name || `Episode ${episodeDetails?.episode_number}`}
                        className="w-[240px] sm:w-[280px] md:w-[360px] rounded-lg shadow-2xl border-2 border-white/10"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-[240px] sm:w-[280px] md:w-[360px] aspect-video rounded-lg shadow-2xl border-2 border-white/10 bg-gradient-to-br from-gray/40 to-gray/20 flex flex-col items-center justify-center ${episodeDetails?.still_path ? 'hidden' : 'flex'}`}>
                      <Film className="text-white/40 mb-2" size={48} />
                      <span className="text-white/60 mont text-sm">S{episodeDetails?.season_number}E{episodeDetails?.episode_number}</span>
                    </div>
                  </div>

                  {/* Episode Info */}
                  <div className="flex-1 text-white">
                    <Link to={`/tvshows/${id}`} className="inline-block mb-2">
                      <h2 className="text-xl md:text-2xl font-semibold poppins hover:text-red transition-colors duration-300">
                        {details?.name}
                      </h2>
                    </Link>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold poppins mb-2">
                      {episodeDetails?.name || `Episode ${episodeDetails?.episode_number}`}
                    </h1>

                    {/* Episode Meta Info */}
                    <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-4">
                      <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
                        <Tv size={16} />
                        <span className="mont font-semibold text-sm">
                          S{episodeDetails?.season_number}E{episodeDetails?.episode_number}
                        </span>
                      </div>
                      
                      {episodeDetails?.vote_average > 0 && (
                        <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
                          <Star className="text-yellow-400" size={16} fill="#FBBF24" />
                          <span className="mont font-semibold text-sm">{episodeDetails?.vote_average?.toFixed(1)}</span>
                        </div>
                      )}
                      
                      {episodeDetails?.runtime > 0 && (
                        <div className="flex items-center gap-2 text-white/90">
                          <Clock size={16} />
                          <span className="mont text-sm">{formatRuntime(episodeDetails.runtime)}</span>
                        </div>
                      )}
                      
                      {episodeDetails?.air_date && (
                        <div className="flex items-center gap-2 text-white/90">
                          <Calendar size={16} />
                          <span className="mont text-sm">{formattedDate(episodeDetails.air_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className={`padding ${episodeDetails?.still_path ? 'pt-8' : 'pt-24'}`}>
          {/* Episode Info Section (when no backdrop) */}
          {!episodeDetails?.still_path && (
            <div className="mb-8">
              <Link to={`/tvshows/${id}`} className="inline-block mb-2">
                <h2 className="text-xl md:text-2xl font-semibold poppins text-white hover:text-red transition-colors duration-300">
                  {details?.name}
                </h2>
              </Link>
              <h1 className="text-3xl md:text-5xl font-bold poppins text-white mb-4">
                {episodeDetails?.name || `Episode ${episodeDetails?.episode_number}`}
              </h1>

              {/* Episode Meta */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="flex items-center gap-2 bg-gray/20 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
                  <Tv size={16} className="text-red" />
                  <span className="mont font-semibold text-white text-sm">
                    S{episodeDetails?.season_number}E{episodeDetails?.episode_number}
                  </span>
                </div>
                
                {episodeDetails?.vote_average > 0 && (
                  <div className="flex items-center gap-2 bg-gray/20 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
                    <Star className="text-yellow-400" size={16} fill="#FBBF24" />
                    <span className="mont font-semibold text-white text-sm">{episodeDetails?.vote_average?.toFixed(1)}</span>
                  </div>
                )}
                
                {episodeDetails?.runtime > 0 && (
                  <div className="flex items-center gap-2 text-white/90">
                    <Clock size={16} />
                    <span className="mont text-sm">{formatRuntime(episodeDetails.runtime)}</span>
                  </div>
                )}
                
                {episodeDetails?.air_date && (
                  <div className="flex items-center gap-2 text-white/90">
                    <Calendar size={16} />
                    <span className="mont text-sm">{formattedDate(episodeDetails.air_date)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Overview */}
          {episodeDetails?.overview && (
            <div className="mb-8">
              <h2 className="text-white poppins text-xl md:text-2xl font-semibold mb-3">Overview</h2>
              <p className="text-white/80 mont text-base md:text-lg leading-relaxed max-w-4xl">
                {episodeDetails?.overview}
              </p>
            </div>
          )}

          {/* Watch Episode Section */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-1 w-12 bg-red"></div>
              <h2 className="text-white poppins text-2xl md:text-3xl font-bold">Watch Episode</h2>
            </div>
            <p className="text-white/60 mont text-sm md:text-base mb-6 max-w-2xl">
              You can switch between Server 1 and Server 2 to enjoy the best TV show playback experience.
            </p>

            {/* Modern Server Selector */}
            <div className="inline-flex bg-gray/20 backdrop-blur-sm border border-white/10 rounded-lg p-1 mb-6">
              <button
                onClick={() => {
                  setServer1(true);
                  setServer2(false);
                }}
                className={`px-6 py-2 rounded-md mont font-semibold text-sm transition-all duration-300 ${
                  server1
                    ? "bg-red text-white shadow-lg shadow-red/30"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Server 1
              </button>
              <button
                onClick={() => {
                  setServer1(false);
                  setServer2(true);
                }}
                className={`px-6 py-2 rounded-md mont font-semibold text-sm transition-all duration-300 ${
                  server2
                    ? "bg-red text-white shadow-lg shadow-red/30"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Server 2
              </button>
            </div>

            {/* Episode Player */}
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black border border-white/10">
              {server1 && (
                <iframe
                  src={`https://vidsrc-embed.ru/embed/tv?tmdb=${id}&season=${season_number}&episode=${episode_number}`}
                  title="Episode Player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              )}
              {server2 && (
                <iframe
                  src={`https://vidsrc.to/embed/tv/${id}/${season_number}/${episode_number}`}
                  title="Episode Player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              )}
            </div>
          </div>

          {/* Seasons and Episodes Section */}
          {allSeasonsData && allSeasonsData.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-1 w-12 bg-red"></div>
                <h2 className="text-white poppins text-2xl md:text-3xl font-bold">
                  {details?.name} - Seasons & Episodes
                </h2>
              </div>
              <div className="bg-gray/10 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
                {allSeasonsData?.map((item) => (
                  <AccordionSeason
                    key={item?.id}
                    s_air_date={item?.air_date}
                    season_number={item?.season_number}
                    name={item?.name}
                    episodes={item?.episodes}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Similar TV Shows Section */}
          {similarMovies && similarMovies.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-1 w-12 bg-red"></div>
                <h2 className="text-white poppins text-2xl md:text-3xl font-bold">Similar TV Shows</h2>
              </div>
              <Swiper
                spaceBetween={20}
                slidesPerView={5}
                loop={similarMovies.length > 5}
                navigation={true}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                breakpoints={{
                  230: {
                    slidesPerView: 2,
                    spaceBetween: 8,
                  },
                  480: {
                    slidesPerView: 3,
                    spaceBetween: 10,
                  },
                  640: {
                    slidesPerView: 3,
                    spaceBetween: 12,
                  },
                  768: {
                    slidesPerView: 4,
                    spaceBetween: 15,
                  },
                  1000: {
                    slidesPerView: 5,
                    spaceBetween: 18,
                  },
                  1280: {
                    slidesPerView: 6,
                    spaceBetween: 20,
                  },
                }}
                modules={[Navigation, Pagination]}
                className="modern-swiper"
              >
                {similarMovies?.filter(show => show.poster_path).map((show) => (
                  <SwiperSlide key={show?.id} className="modern-movie-slide">
                    <Link to={`/tvshows/${show?.id}`} className="group">
                      <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                        <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                          <img
                            src={`https://image.tmdb.org/t/p/w500${show.poster_path}`}
                            alt={show?.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                              <Play className="text-white" size={28} fill="white" />
                            </div>
                          </div>
                          <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                            <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                            <span className="text-white mont text-xs font-semibold">
                              {show?.vote_average?.toFixed(1)}
                            </span>
                          </div>
                        </div>
                        <div className="p-3">
                          <h3 className="text-white mont font-semibold text-sm leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                            {show?.name}
                          </h3>
                        </div>
                      </div>
                    </Link>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {/* Recommended TV Shows Section */}
          {recommendedMovies && recommendedMovies.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-1 w-12 bg-red"></div>
                <h2 className="text-white poppins text-2xl md:text-3xl font-bold">Recommended TV Shows</h2>
              </div>
              <Swiper
                spaceBetween={20}
                slidesPerView={5}
                loop={recommendedMovies.length > 5}
                navigation={true}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                breakpoints={{
                  230: {
                    slidesPerView: 2,
                    spaceBetween: 8,
                  },
                  480: {
                    slidesPerView: 3,
                    spaceBetween: 10,
                  },
                  640: {
                    slidesPerView: 3,
                    spaceBetween: 12,
                  },
                  768: {
                    slidesPerView: 4,
                    spaceBetween: 15,
                  },
                  1000: {
                    slidesPerView: 5,
                    spaceBetween: 18,
                  },
                  1280: {
                    slidesPerView: 6,
                    spaceBetween: 20,
                  },
                }}
                modules={[Navigation, Pagination]}
                className="modern-swiper"
              >
                {recommendedMovies?.filter(show => show.poster_path).map((show) => (
                  <SwiperSlide key={show?.id} className="modern-movie-slide">
                    <Link to={`/tvshows/${show?.id}`} className="group">
                      <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                        <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                          <img
                            src={`https://image.tmdb.org/t/p/w500${show.poster_path}`}
                            alt={show?.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                              <Play className="text-white" size={28} fill="white" />
                            </div>
                          </div>
                          <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                            <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                            <span className="text-white mont text-xs font-semibold">
                              {show?.vote_average?.toFixed(1)}
                            </span>
                          </div>
                        </div>
                        <div className="p-3">
                          <h3 className="text-white mont font-semibold text-sm leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                            {show?.name}
                          </h3>
                        </div>
                      </div>
                    </Link>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default EpisodePage;
